import { Injectable } from '@angular/core';

export enum AIVersionMode {
    Alpha = 1, // internal only
    Beta = 2, // Show Beta AI features
    GA = 3 // Show only GA AI features
}

export enum AIEnablement {
    NotRestricted = 1, // all Gen-AI features available
    Limited = 2, // only Gen-AI features that don't send sensitive AI data
    FullyRestricted = 3 // no Gen-AI features are available
}

export enum AIConsent {
    PendingApproval = 1,
    Approved = 2,
    Rejected = 3
}

export enum AISensitive {
    NotSensitiveData = 1,
    SensitiveData = 2
}

@Injectable({ providedIn: 'root' })
export class AiModeService {
    private isAiVersionMatch(aiFeatureMode: AIVersionMode, customerAccountMode: number): boolean {
        switch (aiFeatureMode) {
            case AIVersionMode.Alpha: {
                return customerAccountMode === AIVersionMode.Alpha;
            }
            case AIVersionMode.Beta: {
                return customerAccountMode === AIVersionMode.Alpha || customerAccountMode === AIVersionMode.Beta;
            }
            case AIVersionMode.GA: {
                return (
                    customerAccountMode === AIVersionMode.Alpha ||
                    customerAccountMode === AIVersionMode.Beta ||
                    customerAccountMode === AIVersionMode.GA
                );
            }
            default: {
                return false;
            }
        }
    }

    private isAiRestrictedMatch(aiFeatureEnablement: AISensitive, customerAccountEnablement: number): boolean {
        switch (aiFeatureEnablement) {
            case AISensitive.NotSensitiveData: {
                return customerAccountEnablement === AIEnablement.Limited || customerAccountEnablement === AIEnablement.NotRestricted;
            }
            case AISensitive.SensitiveData: {
                return customerAccountEnablement === AIEnablement.NotRestricted;
            }
            default: {
                return false;
            }
        }
    }

    private thereIsConsent(customerConsent: number): boolean {
        switch (customerConsent) {
            case AIConsent.Approved:
                return true;
            case AIConsent.PendingApproval:
            case AIConsent.Rejected:
                return false;
            default:
                return false;
        }
    }

    isFeatureEnabled(
        aiFeatureState: { mode: AIVersionMode; enablement: AISensitive },
        aiCustomerAccountState: { mode: number; enablement: number },
        aiConsent: number
    ): boolean {
        if (this.thereIsConsent(aiConsent)) {
            const aiModeMatch = this.isAiVersionMatch(aiFeatureState.mode, aiCustomerAccountState.mode);
            const aiEnablementMatch = this.isAiRestrictedMatch(aiFeatureState.enablement, aiCustomerAccountState.enablement);
            return aiModeMatch && aiEnablementMatch;
        }
        return false;
    }

    isConsentPopupEnabled(aiEnablement: number, aiConsent: number) {
        return aiEnablement != AIEnablement.FullyRestricted && aiConsent === AIConsent.PendingApproval;
    }

    isAccountBetaMode(accountMode: number): boolean {
        return accountMode < AIVersionMode.GA;
    }
}
